import { useState, useEffect, useCallback, useMemo } from 'react';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import { debounce } from 'lodash';
import AuthStore from 'auth-store';

import { useGetProfile } from 'core-modules/profile/hooks';
import { useCheckSavedBusinessInfo } from 'core-modules/merchant-onboarding/hooks';

import * as c from './constants';
import * as actions from './actions';

export const useMerchantList = (params) => {
  const isLoading = useApiLoading(c.MERCHANT_LIST, 'get');
  const data = useApiGet(c.MERCHANT_LIST, []);
  const pager = useApiGet(c.MERCHANT_PAGER, {});
  useEffect(() => {
    actions.listMerchants(params);
  }, [params]);
  return [isLoading, data, pager];
};

export const useMerchantSelected = () => {
  const isLoading = useApiLoading(c.MERCHANT_ITEM, 'get');
  const data = useApiGet(c.MERCHANT_ITEM, {});
  return [isLoading, data];
};

export const useSelectMerchant = (id) => {
  useEffect(() => {
    if (!id) {
      req.set(c.MERCHANT_ITEM, null);
      return;
    }
    actions.selectMerchant(id);
  }, [id]);
};

export const useCreateMerchant = (onSuccess, onError) => {
  const isLoading = useApiLoading(c.MERCHANT_ITEM, 'post');
  // eslint-disable-next-line
  const submit = useCallback(actions.createMerchant(onSuccess, onError), [
    onSuccess,
    onError,
  ]);
  return [isLoading, submit];
};

export const useUpdateMerchant = () => {
  const isLoading = useApiLoading(c.MERCHANT_ITEM, 'put');
  const submit = useCallback((id, payload, onSuccess) => {
    actions.updateMerchant(id, payload, onSuccess);
  }, []);
  return [isLoading, submit];
};

export const useRemoveMerchant = () => {
  const isLoading = useApiLoading(c.MERCHANT_ITEM, 'remove');
  const submit = useCallback((id, onSuccess) => {
    actions.removeMerchant(id, onSuccess);
  }, []);
  return [isLoading, submit];
};

export const useSettlementList = (params) => {
  const isLoading = useApiLoading(c.MERCHANT_SETTLEMENT_LIST, 'get');
  const list = useApiGet(c.MERCHANT_SETTLEMENT_LIST, []);
  const pager = useApiGet(c.MERCHANT_SETTLEMENT_PAGER, {});
  useEffect(() => {
    actions.listMerchantSettlement(params);
  }, [params]);
  return [
    false,
    [
      // FIXME: STATIC DATA
      {
        id: 1,
        created_at_human: 'Aug 26 2021, 04:12 PM',
        refno: 'REF1234',
        billing: 'Aug 24, 2021',
        debit: '1,000',
        credit: '',
        balance: '2,500',
        remarks: 'Static collection',
      },
      {
        id: 2,
        created_at_human: 'Aug 25 2021, 04:10 PM',
        refno: 'REF1233',
        billing: 'Aug 24, 2021',
        debit: '',
        credit: '500',
        balance: '1,500',
        remarks: 'Static settlement',
      },
      {
        id: 3,
        created_at_human: 'Aug 24 2021, 04:05 PM',
        refno: 'REF1233',
        billing: 'Aug 24, 2021',
        debit: '2,000',
        credit: '',
        balance: '2,000',
        remarks: 'Static collection',
      },
    ],
    {},
    isLoading,
    list,
    pager,
  ];
};

export const useSettlementCountCard = (name, filter) => {
  const isLoading = useApiLoading(`${c.COUNT_CARD}/${name}`, 'get');
  const { value } = useApiGet(`${c.COUNT_CARD}/${name}`, {});
  useEffect(() => {
    const action = actions.getCountCard[name];
    if (typeof action === 'function') action(filter);
  }, [name, filter]);
  return [isLoading, value];
};

/* MERCHANT BRANCH */
const STATIC_API_KEYS = {
  1: {
    id: 1,
    name: 'Api Key 1',
    code: 'A233R-RW8Q-NL12-WI92BN1',
  },
};

export const useMerchantApiKeyList = (params) => {
  const isLoading = useApiLoading(c.MERCHANT_API_KEY_LIST, 'get');
  const meta = useApiGet(c.MERCHANT_API_KEY_PAGER, {});
  // const list = useApiGet(c.MERCHANT_API_KEY_LIST, []);
  useEffect(() => {
    actions.listMerchantApiKey(params);
  }, [params]);
  return [isLoading, Object.values(STATIC_API_KEYS), meta];
};

export const useCreateMerchantApiKey = () => {
  const isLoading = useApiLoading(c.MERCHANT_API_KEY_ITEM, 'post');
  const submit = useCallback((payload, onSuccess) => {
    actions.createMerchantApiKey(payload, onSuccess);
  }, []);
  return [isLoading, submit];
};

export const useUpdateMerchantApiKey = () => {
  const isLoading = useApiLoading(c.MERCHANT_API_KEY_ITEM, 'put');
  const submit = useCallback((id, payload, onSuccess) => {
    actions.updateMerchantApiKey(id, payload, onSuccess);
  }, []);
  return [isLoading, submit];
};

export const useDeleteMerchantApiKey = () => {
  const isLoading = useApiLoading(c.MERCHANT_API_KEY_ITEM, 'remove');
  const submit = useCallback((id, onSuccess) => {
    actions.deleteMerchantApiKey(id, onSuccess);
  }, []);
  return [isLoading, submit];
};
/* MERCHANT BRANCH END */

/* MERCHANT PROFILE */
export const useGetGeneralInfo = () => {
  const [, merchant] = useMerchantSelected();
  const isLoading = useApiLoading(c.MERCHANT_GENERAL_INFO, 'get');
  const data = useApiGet(c.MERCHANT_GENERAL_INFO, {});
  useEffect(() => {
    actions.getGeneralInfo(merchant?.id);
  }, [merchant]);
  return [isLoading, data];
};

export const useGetBusinessInfo = () => {
  const [, info] = useGetGeneralInfo();
  const [, checkSavedBusinessInfo] = useCheckSavedBusinessInfo();
  const isLoading = useApiLoading(c.MERCHANT_BUSINESS_INFO, 'get');
  const data = useApiGet(c.MERCHANT_BUSINESS_INFO, {});
  useEffect(() => {
    if (info?.id && info?.business_type) {
      actions.getBusinessInfo(
        info?.id,
        info?.business_type,
        checkSavedBusinessInfo
      );
    }
  }, [info, checkSavedBusinessInfo]);
  return [isLoading, data];
};
/* MERCHANT PROFILE END */

/* MERCHANT CATEGORY & CHANNEL ATTACHMENT START */
export const useMerchantAttachedCategories = (id) => {
  const isLoading = useApiLoading(c.LIST_PAYMENT_CATEGORIES, 'get');
  const data = useApiGet(c.LIST_PAYMENT_CATEGORIES, []);

  const fetchData = useCallback(() => {
    actions.listMerchantAttachedCategories(id);
  }, [id]);

  useEffect(fetchData, [fetchData]);

  return [isLoading, data, fetchData];
};

export const usePaymentCategoryChannelList = (merchantId, categoryId) => {
  const isLoading = useApiLoading(c.LIST_PAYMENT_CATEGORY_CHANNELS, 'get');
  const data = useApiGet(c.LIST_PAYMENT_CATEGORY_CHANNELS(categoryId), []);

  const fetchData = useCallback(() => {
    if (!merchantId || !categoryId) return;
    actions.listPaymentCategoryChannels(merchantId, categoryId);
  }, [merchantId, categoryId]);

  useEffect(fetchData, [fetchData]);

  return [isLoading, data, fetchData];
};

export const useSelectPaymentChannelOptions = (params) => {
  const isLoading = useApiLoading(c.LIST_SELECT_PAYMENT_CHANNELS, 'get');
  const data = useApiGet(c.LIST_SELECT_PAYMENT_CHANNELS, []);

  const debouncedFetcher = useMemo(
    () => debounce(actions.listSelectChannels, 400),
    []
  );

  const reset = useCallback(() => {
    req.set(c.LIST_SELECT_PAYMENT_CHANNELS, []);
  }, []);

  const fetchData = useCallback(() => {
    if (!params?.keyword) return;
    actions.listSelectChannels(params);
  }, [params]);

  useEffect(() => {
    if (!params?.keyword) return;
    debouncedFetcher(params);
  }, [params, debouncedFetcher]);

  return [isLoading, data, fetchData, reset];
};

export const useTogglePaymentCategoryStatus = (
  initStatus,
  merchantId,
  categoryId,
  callback
) => {
  const [status, setStatus] = useState(initStatus);
  const isLoadingStatus = useApiLoading(c.TOGGLE_CATEGORY_STATUS, 'put');
  const activateCategory = useCallback(() => {
    setStatus('ACTIVE');
    actions.activateMerchantPaymentCategory(
      merchantId,
      categoryId,
      callback,
      () => setStatus('INACTIVE')
    );
  }, [merchantId, categoryId, callback, setStatus]);
  const deactivateCategory = useCallback(() => {
    setStatus('INACTIVE');
    actions.deactivateMerchantPaymentCategory(
      merchantId,
      categoryId,
      callback,
      () => setStatus('ACTIVE')
    );
  }, [merchantId, categoryId, callback, setStatus]);

  useEffect(() => {
    setStatus(initStatus);
  }, [initStatus]);

  return { status, isLoadingStatus, activateCategory, deactivateCategory };
};

export const useTogglePaymentChannelStatus = (
  initStatus,
  merchantId,
  categoryId,
  channelId,
  callback
) => {
  const [status, setStatus] = useState(initStatus);
  const isLoadingStatus = useApiLoading(c.TOGGLE_CATEGORY_STATUS, 'put');
  const activateChannel = useCallback(() => {
    setStatus('ACTIVE');
    actions.toggleAttachedChannelStatus(
      merchantId,
      categoryId,
      channelId,
      { status: 'ACTIVE' },
      callback,
      () => setStatus('INACTIVE')
    );
  }, [merchantId, categoryId, channelId, callback, setStatus]);
  const deactivateChannel = useCallback(() => {
    setStatus('INACTIVE');
    actions.toggleAttachedChannelStatus(
      merchantId,
      categoryId,
      channelId,
      { status: 'INACTIVE' },
      callback,
      () => setStatus('ACTIVE')
    );
  }, [merchantId, categoryId, channelId, callback, setStatus]);

  useEffect(() => {
    setStatus(initStatus);
  }, [initStatus]);

  return { status, isLoadingStatus, activateChannel, deactivateChannel };
};
/* MERCHANT CATEGORY & CHANNEL ATTACHMENT END */

/* BILLER AND ORGANIZATION SETTLEMENT */
export const useBillerSettlementList = () => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(
    `${c.MERCHANT_SETTLEMENT_LIST}/biller`,
    'get'
  );
  const data = useApiGet(`${c.MERCHANT_SETTLEMENT_LIST}/biller`, {});
  const pager = useApiGet(`${c.MERCHANT_SETTLEMENT_PAGER}/biller`, {});
  const fetchData = useCallback(
    (params) => {
      actions.billerSettlementList(profile?.biller_id, params);
    },
    [profile]
  );
  // useEffect(fetchData, [fetchData]);
  return [isLoading, data, pager, fetchData];
};

export const useOrgSettlementList = () => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(
    `${c.MERCHANT_ORG_SETTLEMENT_LIST}/org`,
    'get'
  );
  const data = useApiGet(`${c.MERCHANT_ORG_SETTLEMENT_LIST}/org`, {});
  const pager = useApiGet(`${c.MERCHANT_ORG_SETTLEMENT_PAGER}/org`, {});
  const fetchData = useCallback(
    (params) => {
      actions.orgSettlementList(profile?.organization_id, params);
    },
    [profile]
  );

  // useEffect(fetchData, [fetchData]);
  return [isLoading, data, pager, fetchData];
};

export const useSelectedBillerSettlement = () => {
  const isLoading = useApiLoading(`${c.MERCHANT_SETTLEMENT_GET}/biller`, 'get');
  const data = useApiGet(`${c.MERCHANT_SETTLEMENT_GET}/biller`, {});
  const userRole = AuthStore.get('_role');
  const fetchData = useCallback(
    (merchantId, settlementId) => {
      if (userRole === 'biller' && merchantId && settlementId) {
        actions.selectedBillerSettlement(merchantId, settlementId);
      }
      if (userRole === 'organization' && merchantId && settlementId) {
        actions.selectedOrgSettlement(merchantId, settlementId);
      }
    },
    [userRole]
  );
  useEffect(fetchData, [fetchData]);
  return [isLoading, data, fetchData];
};

export const useAddBillerSettlement = () => {
  const [profile] = useGetProfile();
  const isLoading = useApiLoading(c.MERCHANT_SETTLEMENT_ADD, 'post');
  const submit = useCallback(
    (payload, callback) => {
      actions.addBillerSettlement(profile?.biller_id, payload, callback);
    },
    [profile]
  );
  return [isLoading, submit];
};
/* BILLER AND ORGANIZATION SETTLEMENT END */
